import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  alpha,
  useTheme,
  darken,
} from '@mui/material'
import {
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material'

interface InfoPopupProps {
  open: boolean
  onClose: () => void
  title?: string
  message?: string | React.ReactNode
  closeText?: string
  buttonColor?: string
}

/**
 * A reusable info dialog with a modern, premium design.
 */
const InfoPopup: React.FC<InfoPopupProps> = ({
  open,
  onClose,
  title = 'Information',
  message = '',
  closeText = 'Close',
  buttonColor = 'info',
}) => {
  const theme = useTheme()

  const getThemeColor = React.useMemo(() => {
    if (buttonColor && (theme.palette as any)[buttonColor]) {
      return (theme.palette as any)[buttonColor].main
    }
    if (buttonColor && buttonColor !== 'info') return buttonColor
    return theme.palette.info.main
  }, [buttonColor, theme])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow:
            '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <Box sx={{ height: 6, width: '100%', bgcolor: getThemeColor }} />

      <DialogTitle
        sx={{
          m: 0,
          p: 1.5,
          pb: 0,
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: theme.palette.grey[400],
            '&:hover': {
              color: theme.palette.grey[600],
              bgcolor: alpha(theme.palette.grey[200], 0.4),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 4, pb: 1, textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: alpha(getThemeColor, 0.1),
              mb: 0.5,
            }}
          >
            <InfoIcon sx={{ fontSize: 40, color: getThemeColor }} />
          </Box>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {message}
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          p: 4,
          pt: 3,
          justifyContent: 'center',
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          fullWidth
          sx={{
            py: 1.2,
            borderRadius: 2.5,
            bgcolor: getThemeColor,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.95rem',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: darken(getThemeColor, 0.1),
              boxShadow: '0 4px 12px ' + alpha(getThemeColor, 0.3),
            },
          }}
        >
          {closeText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default InfoPopup
